import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  Divider,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import HowToVote from '@mui/icons-material/HowToVote';
import { useTranslation } from 'react-i18next';
import { GlassDialog, GlassButton } from './ui';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { pollApi } from '../api/poll.service';
import { getErrorMessage } from '../api/utils/error-handler';
import { DIALOG, FONT_SIZES } from '../utils/responsive';
import { TEXT_COLORS, BG_COLORS, BORDER_COLORS } from '../utils/colors';
import type { PollDto } from '../types';

const MandatoryPollDialog: React.FC = () => {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const { showSuccess, showError } = useSnackbar();

  const [unvotedPolls, setUnvotedPolls] = useState<PollDto[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null);
  const [voting, setVoting] = useState(false);

  const shouldShow = user?.hasUnvotedPolls === true;
  const poll = unvotedPolls[currentIndex] ?? null;

  // Load all unvoted polls in one request
  const loadUnvotedPolls = useCallback(async () => {
    setLoading(true);
    try {
      const polls = await pollApi.getUnvotedPolls();
      setUnvotedPolls(polls);
      setCurrentIndex(0);
      setSelectedOptionId(null);
      if (polls.length === 0) {
        await refreshUser();
      }
    } catch {
      setUnvotedPolls([]);
    } finally {
      setLoading(false);
    }
  }, [refreshUser]);

  useEffect(() => {
    if (shouldShow) {
      loadUnvotedPolls();
    }
  }, [shouldShow, loadUnvotedPolls]);

  const handleVote = useCallback(async () => {
    if (!poll || selectedOptionId === null) return;
    setVoting(true);
    try {
      await pollApi.vote(poll.id, { optionId: selectedOptionId });
      showSuccess(t('poll:messages.voteSuccess'));

      const nextIndex = currentIndex + 1;
      if (nextIndex < unvotedPolls.length) {
        // Show next poll from the already loaded list
        setCurrentIndex(nextIndex);
        setSelectedOptionId(null);
      } else {
        // All done — refresh user to clear flag
        setUnvotedPolls([]);
        await refreshUser();
      }
    } catch (err) {
      showError(getErrorMessage(err, t('poll:messages.voteError')));
    } finally {
      setVoting(false);
    }
  }, [poll, selectedOptionId, currentIndex, unvotedPolls.length, refreshUser, showSuccess, showError, t]);

  const isOpen = shouldShow && (loading || poll !== null);

  if (!isOpen) return null;

  return (
    <GlassDialog
      open
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      onClose={(_e, reason) => {
        // Block all close attempts
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.85)' },
        },
      }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
          <CircularProgress size={40} sx={{ color: TEXT_COLORS.MUTED }} />
        </Box>
      ) : poll && (
        <>
          <DialogTitle sx={{
            px: DIALOG.padding,
            pt: DIALOG.padding,
            pb: 1,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
          }}>
            <HowToVote sx={{ color: '#dc2626', fontSize: { xs: 20, sm: 24 }, mt: 0.3 }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{
                fontWeight: 700,
                fontSize: DIALOG.titleFontSize,
                color: TEXT_COLORS.PRIMARY,
                lineHeight: 1.3,
              }}>
                {poll.title}
              </Typography>
              <Typography sx={{
                color: TEXT_COLORS.TERTIARY,
                fontSize: FONT_SIZES.smallCaption,
                mt: 0.5,
              }}>
                {t('poll:mandatory.subtitle')}
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ px: DIALOG.padding, py: 1 }}>
            {poll.description && (
              <Typography sx={{
                color: TEXT_COLORS.SECONDARY,
                fontSize: DIALOG.contentFontSize,
                mb: 2,
                lineHeight: 1.6,
              }}>
                {poll.description}
              </Typography>
            )}

            <Divider sx={{ borderColor: BORDER_COLORS.DEFAULT, mb: 2 }} />

            <Typography sx={{
              color: TEXT_COLORS.MUTED,
              fontSize: FONT_SIZES.smallCaption,
              mb: 1.5,
              fontWeight: 600,
            }}>
              {t('poll:detail.selectOption')}
            </Typography>

            <RadioGroup
              value={selectedOptionId ?? ''}
              onChange={(e) => setSelectedOptionId(Number(e.target.value))}
            >
              {poll.options
                .sort((a, b) => a.ordering - b.ordering)
                .map(option => (
                  <FormControlLabel
                    key={option.id}
                    value={option.id}
                    control={
                      <Radio
                        sx={{
                          color: TEXT_COLORS.TERTIARY,
                          '&.Mui-checked': { color: '#dc2626' },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{
                        color: TEXT_COLORS.PRIMARY,
                        fontSize: DIALOG.contentFontSize,
                      }}>
                        {option.text}
                      </Typography>
                    }
                    sx={{
                      mx: 0,
                      mb: 0.5,
                      borderRadius: 2,
                      py: 0.5,
                      px: 1,
                      transition: 'background 0.2s',
                      '&:hover': { background: BG_COLORS.GLASS },
                      ...(selectedOptionId === option.id && {
                        background: BG_COLORS.GLASS_LIGHT,
                        border: `1px solid ${BORDER_COLORS.LIGHTER}`,
                      }),
                      ...(!selectedOptionId || selectedOptionId !== option.id ? {
                        border: '1px solid transparent',
                      } : {}),
                    }}
                  />
                ))}
            </RadioGroup>
          </DialogContent>

          <DialogActions sx={{
            px: DIALOG.padding,
            pb: DIALOG.padding,
            pt: 1,
            flexDirection: 'column',
            gap: 1,
          }}>
            <GlassButton
              fullWidth
              onClick={handleVote}
              disabled={selectedOptionId === null || voting}
              sx={{ height: 36, fontSize: FONT_SIZES.button }}
            >
              {voting ? (
                <CircularProgress size={18} sx={{ color: 'inherit' }} />
              ) : (
                <>
                  <HowToVote sx={{ fontSize: 18, mr: 0.75 }} />
                  {t('poll:detail.voteButton')}
                </>
              )}
            </GlassButton>
          </DialogActions>
        </>
      )}
    </GlassDialog>
  );
};

export default MandatoryPollDialog;
